import React, { useState, useEffect, useCallback } from 'react';
import planejamentoCapacidadeService from '../services/planejamentoCapacidade.service';
import AlocarHorasModal               from './AlocarHorasModal';
import CalendarioCapacidade           from './CalendarioCapacidade';

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  verde:   '#1D9E75', verdeBg: '#f0fff4',
  vermelho:'#e53935', vermBg:  '#fff0f0',
  amarelo: '#d97706', amlBg:   '#fffbeb',
  borda:   '#e8eaed', cinzaBg: '#fafafa',
  texto:   '#1a1a2e', muted:   '#888',
  branco:  '#fff',
};

// ════════════════════════════════════════════════════════════════════════════
// SessaoCapacidade — renderiza dentro do PlanejamentoModal
// ────────────────────────────────────────────────────────────────────────────
// Props:
//   negocio (objeto do projeto), usuario, refreshKey (pra forçar reload)
// ════════════════════════════════════════════════════════════════════════════
function SessaoCapacidade({ negocio, usuario, refreshKey }) {
  const [analistas, setAnalistas]  = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro]             = useState('');

  // Edição inline da data de início
  const [editandoDataDe, setEditandoDataDe] = useState(null); // colaborador_id
  const [novaData, setNovaData]             = useState('');
  const [novaHora, setNovaHora]             = useState('09:00');

  // Modal de alocação
  const [alocarModalAberto, setAlocarModalAberto] = useState(false);
  const [analistaParaAlocar, setAnalistaParaAlocar] = useState(null);

  // Calendário expandido inline (1 por vez — guarda o colaborador_id ou null)
  const [calendarioAbertoDe, setCalendarioAbertoDe] = useState(null);
  // Key incrementada quando uma alocação é criada/excluída — força reload do calendário aberto
  const [calendarioRefreshKey, setCalendarioRefreshKey] = useState(0);
  const recarregarCalendario = () => setCalendarioRefreshKey(k => k + 1);

  // Mensagem de sucesso temporária
  const [msg, setMsg] = useState('');

  const carregar = useCallback(async () => {
    if (!negocio?.id) return;
    setCarregando(true);
    setErro('');
    try {
      const r = await planejamentoCapacidadeService.resumo(negocio.id);
      setAnalistas(r);
    } catch (err) {
      console.error('[SessaoCapacidade] erro ao carregar:', err);
      setErro('Erro ao carregar resumo: ' + (err.response?.data?.mensagem || err.message));
    } finally {
      setCarregando(false);
    }
  }, [negocio?.id]);

  useEffect(() => { carregar(); }, [carregar, refreshKey]);

  // ─── Edição inline da data de início ─────────────────────────────────────
  const abrirEditarData = (a) => {
    setEditandoDataDe(a.colaborador_id);
    if (a.data_inicio) {
      const d = new Date(a.data_inicio);
      setNovaData(d.toISOString().slice(0, 10));
      setNovaHora(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`);
    } else {
      setNovaData(new Date().toISOString().slice(0, 10));
      setNovaHora('09:00');
    }
  };

  const cancelarEditarData = () => {
    setEditandoDataDe(null);
    setNovaData('');
    setNovaHora('09:00');
  };

  const salvarData = async (a) => {
    if (!novaData) return;
    try {
      await planejamentoCapacidadeService.upsertDataInicio(negocio.id, a.colaborador_id, {
        data_inicio: `${novaData}T${novaHora}:00`,
        usuario_id:  usuario?.id || null,
      });
      setEditandoDataDe(null);
      await carregar();
      setMsg(`✓ Data de início de ${a.nome_amigavel || a.nome} atualizada.`);
      setTimeout(() => setMsg(''), 2500);
    } catch (err) {
      setErro('Erro ao salvar data: ' + (err.response?.data?.mensagem || err.message));
    }
  };

  const removerData = async (a) => {
    if (!a.data_inicio) return;
    if (!window.confirm(`Remover data de início de ${a.nome_amigavel || a.nome}?`)) return;
    try {
      await planejamentoCapacidadeService.removerDataInicio(negocio.id, a.colaborador_id);
      await carregar();
      setMsg(`✓ Data removida.`);
      setTimeout(() => setMsg(''), 2500);
    } catch (err) {
      setErro('Erro ao remover data: ' + (err.response?.data?.mensagem || err.message));
    }
  };

  // ─── Modal de alocação ──────────────────────────────────────────────────
  const abrirAlocar = (a) => {
    setAnalistaParaAlocar(a);
    setAlocarModalAberto(true);
  };

  const handleAplicado = async (resultado) => {
    setAlocarModalAberto(false);
    await carregar();
    // Se o calendário desse analista está aberto, recarrega ele também
    recarregarCalendario();
    const nBlocos = resultado.blocos?.length || 0;
    const horas = resultado.horasAlocadas || 0;
    setMsg(`✓ ${nBlocos} bloco(s) criado(s) (${horas}h alocadas).`);
    setTimeout(() => setMsg(''), 4000);
  };

  // ─── Helpers ────────────────────────────────────────────────────────────
  const formatarData = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR');
  };
  const formatarDataHora = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const pctAlocado = (a) => {
    if (!a.horas_previstas || a.horas_previstas <= 0) return 0;
    return Math.min(100, Math.round((a.horas_alocadas / a.horas_previstas) * 100));
  };

  if (carregando && analistas.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: COR.muted, fontSize: '12px', fontFamily: "'Manrope', sans-serif" }}>
        Carregando resumo de capacidade...
      </div>
    );
  }

  if (analistas.length === 0) {
    return (
      <div style={{
        padding: '20px', textAlign: 'center', color: COR.muted, fontSize: '12px',
        border: `1px dashed ${COR.borda}`, borderRadius: '6px',
        fontFamily: "'Manrope', sans-serif",
      }}>
        Adicione analistas técnicos ao projeto na sessão acima.
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Manrope', sans-serif" }}>
      {msg && (
        <div style={{
          marginBottom: '10px', padding: '8px 12px',
          backgroundColor: COR.verdeBg, color: COR.verde,
          borderRadius: '5px', fontSize: '12px',
          border: `1px solid ${COR.verde}33`,
        }}>{msg}</div>
      )}
      {erro && (
        <div style={{
          marginBottom: '10px', padding: '8px 12px',
          backgroundColor: COR.vermBg, color: COR.vermelho,
          borderRadius: '5px', fontSize: '12px',
          border: `1px solid ${COR.vermelho}33`,
        }}>{erro}</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {analistas.map(a => {
          const pct = pctAlocado(a);
          const corBarra = pct === 100 ? COR.verde : (pct > 0 ? COR.azul : COR.borda);
          const editando = editandoDataDe === a.colaborador_id;

          return (
            <div key={a.colaborador_id} style={{
              border: `1px solid ${COR.borda}`, borderRadius: '6px',
              backgroundColor: COR.branco, padding: '14px 16px',
            }}>
              {/* Header do card */}
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: '12px',
                marginBottom: '10px',
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: COR.texto }}>
                    {a.nome_amigavel || a.nome}
                  </div>
                  {a.nome_amigavel && (
                    <div style={{ fontSize: '11px', color: COR.muted, marginTop: '2px' }}>
                      {a.nome}
                    </div>
                  )}
                </div>

                {/* Data de início — modo visualização ou edição */}
                <div style={{ minWidth: '230px' }}>
                  {!editando ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
                      <span style={{ fontSize: '11px', color: COR.muted }}>Início:</span>
                      <span style={{
                        fontSize: '12px', fontWeight: '600',
                        color: a.data_inicio ? COR.azul : COR.muted,
                        fontStyle: a.data_inicio ? 'normal' : 'italic',
                      }}>
                        {a.data_inicio ? formatarDataHora(a.data_inicio) : 'não definido'}
                      </span>
                      <button onClick={() => abrirEditarData(a)} title="Editar data de início" style={{
                        border: 'none', background: 'transparent', cursor: 'pointer',
                        color: COR.azul, fontSize: '13px', padding: '2px 4px',
                      }}>✏️</button>
                      {a.data_inicio && (
                        <button onClick={() => removerData(a)} title="Remover data" style={{
                          border: 'none', background: 'transparent', cursor: 'pointer',
                          color: COR.vermelho, fontSize: '13px', padding: '2px 4px',
                        }}>🗑️</button>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'flex-end', alignItems: 'center' }}>
                      <input type="date" value={novaData}
                        onChange={e => setNovaData(e.target.value)}
                        style={{
                          padding: '4px 8px', fontSize: '12px',
                          border: `1px solid ${COR.borda}`, borderRadius: '4px',
                          width: '125px',
                        }} />
                      <input type="time" value={novaHora}
                        onChange={e => setNovaHora(e.target.value)}
                        style={{
                          padding: '4px 8px', fontSize: '12px',
                          border: `1px solid ${COR.borda}`, borderRadius: '4px',
                          width: '85px',
                        }} />
                      <button onClick={() => salvarData(a)} title="Salvar" style={{
                        padding: '4px 8px', fontSize: '11px', fontWeight: '600',
                        border: 'none', borderRadius: '4px',
                        backgroundColor: COR.verde, color: COR.branco, cursor: 'pointer',
                      }}>✓</button>
                      <button onClick={cancelarEditarData} title="Cancelar" style={{
                        padding: '4px 8px', fontSize: '11px', fontWeight: '600',
                        border: `1px solid ${COR.borda}`, borderRadius: '4px',
                        backgroundColor: COR.branco, color: COR.texto, cursor: 'pointer',
                      }}>×</button>
                    </div>
                  )}
                </div>
              </div>

              {/* Stats: previstas / alocadas / pendentes / prazo */}
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '12px', marginBottom: '10px',
              }}>
                <StatComAviso
                  label="PREVISTAS"
                  valor={`${a.horas_previstas}h`}
                  cor={COR.texto}
                  aviso={a.itens_sem_execucao > 0
                    ? `${a.itens_sem_execucao} item(ns) sem horas de execução definidas — não contam nas previstas`
                    : null}
                />
                <Stat label="ALOCADAS"  valor={`${a.horas_alocadas}h`}  cor={a.horas_alocadas > 0 ? COR.verde : COR.muted} />
                <Stat label="PENDENTES" valor={`${a.horas_pendentes}h`} cor={a.horas_pendentes > 0 ? COR.amarelo : COR.muted} />
                <Stat label="PRAZO" valor={a.prazo_final ? formatarData(a.prazo_final) : '—'} cor={COR.texto} />
              </div>

              {/* Aviso quando há itens sem horas de execução definidas */}
              {a.itens_sem_execucao > 0 && (
                <div style={{
                  marginBottom: '10px', padding: '6px 10px',
                  backgroundColor: COR.amlBg,
                  border: `1px solid ${COR.amarelo}33`,
                  borderRadius: '4px',
                  fontSize: '11px', color: COR.amarelo,
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}>
                  <span>⚠</span>
                  <span>
                    <b>{a.itens_sem_execucao}</b> de <b>{a.itens_total}</b> item(ns) atribuído(s)
                    sem <b>horas de execução</b> definidas. Defina na sessão "Horas de Execução"
                    pra que entrem no cálculo de capacidade.
                  </span>
                </div>
              )}

              {/* Barra de progresso */}
              <div style={{
                position: 'relative', height: '8px', backgroundColor: COR.cinzaBg,
                borderRadius: '4px', overflow: 'hidden', marginBottom: '10px',
              }}>
                <div style={{
                  width: `${pct}%`, height: '100%',
                  backgroundColor: corBarra, transition: 'width 0.3s',
                }} />
              </div>

              {/* Botões de ação */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button onClick={() => abrirAlocar(a)}
                  disabled={a.horas_previstas <= 0}
                  title={a.horas_previstas <= 0 ? 'Atribua itens ao analista primeiro' : ''}
                  style={{
                    padding: '7px 14px', fontSize: '12px', fontWeight: '600',
                    border: 'none', borderRadius: '5px',
                    backgroundColor: a.horas_previstas <= 0 ? '#cccccc' : COR.azul,
                    color: COR.branco,
                    cursor: a.horas_previstas <= 0 ? 'not-allowed' : 'pointer',
                  }}>
                  + Alocar horas
                </button>
                <button onClick={() => setCalendarioAbertoDe(
                  calendarioAbertoDe === a.colaborador_id ? null : a.colaborador_id
                )}
                  style={{
                    padding: '7px 14px', fontSize: '12px', fontWeight: '600',
                    border: `1px solid ${COR.azul}`, borderRadius: '5px',
                    backgroundColor: calendarioAbertoDe === a.colaborador_id ? COR.azul : COR.branco,
                    color: calendarioAbertoDe === a.colaborador_id ? COR.branco : COR.azul,
                    cursor: 'pointer',
                  }}>
                  {calendarioAbertoDe === a.colaborador_id
                    ? '▲ Recolher calendário'
                    : '📅 Ver no calendário'}
                </button>
              </div>

              {/* Calendário inline expandido */}
              {calendarioAbertoDe === a.colaborador_id && (
                <div style={{
                  marginTop: '14px', paddingTop: '14px',
                  borderTop: `1px solid ${COR.borda}`,
                }}>
                  <CalendarioCapacidade
                    colaboradorId={a.colaborador_id}
                    negocioId={negocio?.id}
                    refreshKey={calendarioRefreshKey}
                    onAlocacaoExcluida={async () => {
                      await carregar();
                      recarregarCalendario();
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Modal de alocação */}
      <AlocarHorasModal
        aberto={alocarModalAberto}
        analista={analistaParaAlocar}
        negocio={negocio}
        usuario={usuario}
        onCancelar={() => setAlocarModalAberto(false)}
        onAplicado={handleAplicado}
      />
    </div>
  );
}

// ─── Componente Stat (label + valor) ─────────────────────────────────────
function Stat({ label, valor, cor }) {
  return (
    <div>
      <div style={{ fontSize: '10px', color: COR.muted, fontWeight: '600' }}>
        {label}
      </div>
      <div style={{
        fontSize: '14px', fontWeight: '600',
        color: cor || COR.texto, marginTop: '2px',
      }}>
        {valor}
      </div>
    </div>
  );
}

// ─── Stat com indicador de aviso ao lado do label ──────────────────────────
function StatComAviso({ label, valor, cor, aviso }) {
  return (
    <div>
      <div style={{
        fontSize: '10px', color: COR.muted, fontWeight: '600',
        display: 'flex', alignItems: 'center', gap: '4px',
      }}>
        {label}
        {aviso && (
          <span title={aviso} style={{
            color: COR.amarelo, fontSize: '11px', cursor: 'help',
          }}>⚠</span>
        )}
      </div>
      <div style={{
        fontSize: '14px', fontWeight: '600',
        color: cor || COR.texto, marginTop: '2px',
      }}>
        {valor}
      </div>
    </div>
  );
}

export default SessaoCapacidade;
